import { Buffer } from "buffer";
import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Signature } from "@phosphor-icons/react";

import { Task } from "api/types/users";
import { Appointment } from "api/types/appointments";
import { ANSignature, ANLoadingScreen } from "@answersnow/pkg-theme";
import useGetAppointmentInfo from "hooks/api/appointments/useGetAppointmentInfo";
import useUpdateVideoCallSignature from "hooks/api/appointments/useUpdateVideoCallSignature";
import useUploadImage from "hooks/useUploadImage";
import { UserTaskType } from "../Interfaces";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import AppointmentCard from "./AppoinmentCard";

interface CallSignatureTaskProps {
  data: Task;
  setCurrentTask: (task: Task | null) => void;
  onSubmit: () => void;
}

const CallSignatureTask = ({ data, setCurrentTask, onSubmit }: CallSignatureTaskProps) => {
  const { t, i18n } = useTranslation(["dashboard"]);
  const { type: taskType, metadata, id: taskId } = data;
  const signatureLanguage = i18n.language?.startsWith("es") ? "es" : "en";

  const signatureRef = useRef<SignatureRef>(null);

  const [open, setOpen] = useState(true);
  const [isValidSignature, setIsValidSignature] = useState(false);

  const { appointment, isAppointmentLoading } = useGetAppointmentInfo(metadata.videoCallId);

  // Update Appointment with Signature Mutation
  const { updateVideoCallSignature, isUpdatingSignature } = useUpdateVideoCallSignature({
    onSuccess: () => {
      // Refresh task list
      onSubmit();
      // Close modal
      setOpen(false);
    },
  });

  // Upload Signature Image Mutation
  const { uploadImage, isUploading } = useUploadImage({
    onError: (error) => {
      console.error("Error uploading image:", error);
    },
    onSuccess: async (dataUploaded: any) => {
      // Update appointment with signature
      await updateVideoCallSignature({
        videoId: metadata.videoCallId,
        startSignatureURL: dataUploaded?.Location || "",
        taskId,
      });
    },
  });

  if (taskType !== UserTaskType.VIDEO_CALL_SIGNATURE) {
    console.warn(`CallSignatureTask: Task type ${taskType} is not supported`);
    return null;
  }

  const handleExitedModal = () => {
    setCurrentTask(null);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleSubmitSignature = async () => {
    if (signatureRef.current) {
      const signature = signatureRef.current.getTrimmedCanvas().toDataURL();
      const base64Signature = signature.replace(/^data:image\/\w+;base64,/, "");

      const signatureFile = Buffer.from(base64Signature, "base64");

      await uploadImage({
        filename: `${metadata.videoCallId}_signature_client_start.png`,
        file: signatureFile,
        type: "image/png",
      });
    }
  };

  const handleOnSignatureChange = (value?: string, type?: string) => {
    if (signatureRef.current) {
      const isEmptySignature = signatureRef.current.isEmpty();

      if (type === "typed") {
        setIsValidSignature(Boolean(value));
      } else {
        setIsValidSignature(!isEmptySignature);
      }
    }
  };

  return (
    <ResponsiveModal
      open={open}
      title={t("dashboard:parent.myTasks.callSignature.title")}
      onClose={handleCloseModal}
      onOpen={() => setOpen(true)}
      onTransitionExited={handleExitedModal}
      maxWidth="xs"
      drawerSx={{
        height: "86vh",
      }}
      drawerContentSx={{
        height: "calc(100% - 124px)",
      }}
      dialogContentSx={{
        padding: 0,
        margin: "0 8px",
      }}
    >
      <Box>
        <ANSignature
          width={400}
          height={150}
          options={{ resizeCanvas: true }}
          signaturePadRef={signatureRef}
          onSignatureChange={handleOnSignatureChange}
          language={signatureLanguage}
        />

        {isAppointmentLoading ? (
          <Box sx={{ backgroundColor: "grey.50", borderRadius: 2, p: 6, my: 7, height: "146px" }}>
            <ANLoadingScreen height="100%" />
          </Box>
        ) : (
          <AppointmentCard appointmentData={appointment as Appointment} />
        )}

        <Typography variant="body2" color="text.secondary">
          {t("dashboard:parent.myTasks.callSignature.attestation")}
        </Typography>

        <Box sx={{ display: "flex", mt: 7 }}>
          <LoadingButton
            variant="contained"
            color="primary"
            endIcon={<Signature format="outline" />}
            fullWidth
            onClick={handleSubmitSignature}
            disabled={!isValidSignature}
            loading={isUploading || isUpdatingSignature}
          >
            {t("dashboard:parent.myTasks.callSignature.submitButton")}
          </LoadingButton>
        </Box>
      </Box>
    </ResponsiveModal>
  );
};

export default CallSignatureTask;
